import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import salesApiClient from '@/api/salesClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogOverlay } from '@/components/ui/dialog';
import { Plus, FileText, Clock, DollarSign, TrendingUp, AlertTriangle, LayoutGrid, List, Search } from 'lucide-react';
import { format, differenceInDays } from 'date-fns';

export default function ContractsPage() {
  const [contracts, setContracts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [users, setUsers] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterUser, setFilterUser] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [formData, setFormData] = useState({
    contact_id: '',
    contract_type: 'staffing',
    duration_months: '12',
    start_date: '',
    end_date: '',
    contract_value: '',
    staff_cost: '',
    billing_frequency: 'monthly',
    staff_count: '1',
  });

  useEffect(() => {
    fetchContracts();
    fetchContacts();
    fetchUsers();
  }, []);

  const fetchContracts = async () => {
    try {
      const response = await salesApiClient.get('/contracts/active');
      setContracts(response.data);
    } catch (error) {
      toast.error('Failed to fetch contracts');
    }
  };

  const fetchContacts = async () => {
    try {
      const response = await salesApiClient.get('/contacts');
      setContacts(response.data);
    } catch (error) {
      console.error('Failed to fetch contacts');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await salesApiClient.get('/team/members');
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users');
    }
  };

  const getFilteredContracts = () => {
    return contracts.filter(contract => {
      const matchesSearch = searchTerm === '' || 
        contract.contact_company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contract.contact_name?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesUser = filterUser === '' || contract.assigned_to === filterUser || contract.created_by === filterUser;
      
      // Date filtering
      let matchesDate = true;
      if (filterDateFrom || filterDateTo) {
        const contractDate = new Date(contract.start_date);
        if (filterDateFrom) {
          const fromDate = new Date(filterDateFrom);
          fromDate.setHours(0, 0, 0, 0);
          matchesDate = matchesDate && contractDate >= fromDate;
        }
        if (filterDateTo) {
          const toDate = new Date(filterDateTo);
          toDate.setHours(23, 59, 59, 999);
          matchesDate = matchesDate && contractDate <= toDate;
        }
      }
      
      return matchesSearch && matchesUser && matchesDate;
    });
  };

  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user?.name || 'Unknown';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await salesApiClient.post('/contracts', {
        ...formData,
        duration_months: parseInt(formData.duration_months),
        contract_value: parseFloat(formData.contract_value),
        staff_cost: parseFloat(formData.staff_cost),
        staff_count: parseInt(formData.staff_count),
      });
      toast.success('Contract created successfully');
      setIsDialogOpen(false);
      setFormData({
        contact_id: '',
        contract_type: 'staffing',
        duration_months: '12',
        start_date: '',
        end_date: '',
        contract_value: '',
        staff_cost: '',
        billing_frequency: 'monthly',
        staff_count: '1',
      });
      fetchContracts();
    } catch (error) {
      toast.error('Failed to create contract');
    }
  };

  const getExpiryColor = (daysRemaining) => {
    if (daysRemaining <= 30) return 'text-red-400 bg-red-500/20';
    if (daysRemaining <= 60) return 'text-orange-400 bg-orange-500/20';
    if (daysRemaining <= 90) return 'text-amber-400 bg-amber-500/20';
    return 'text-green-400 bg-green-500/20';
  };

  const getMarginColor = (marginPercent) => {
    if (marginPercent >= 30) return 'text-green-400';
    if (marginPercent >= 20) return 'text-blue-400';
    if (marginPercent >= 10) return 'text-amber-400';
    return 'text-red-400';
  };

  return (
    <div className="p-6 md:p-8 lg:p-12 space-y-8 fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-white" data-testid="contracts-title">
            Active Contracts
          </h1>
          <p className="text-base text-slate-400 mt-2">Manage staffing & project contracts</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-contract-button">
              <Plus size={20} className="mr-2" />
              New Contract
            </Button>
          </DialogTrigger>
          <DialogOverlay className="bg-black/80" />
          <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-2xl" data-testid="add-contract-dialog">
            <DialogHeader>
              <DialogTitle className="text-white">Create New Contract</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="contact" className="text-slate-200">Client</Label>
                  <select
                    id="contact"
                    value={formData.contact_id}
                    onChange={(e) => setFormData({ ...formData, contact_id: e.target.value })}
                    className="flex h-10 w-full rounded-md border border-slate-700 bg-slate-800 text-white px-3 py-2 text-sm"
                    required
                    data-testid="contract-contact-select"
                  >
                    <option value="">Select client</option>
                    {contacts.map((contact) => (
                      <option key={contact.id} value={contact.id}>
                        {contact.company} - {contact.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contract_type" className="text-slate-200">Contract Type</Label>
                  <select
                    id="contract_type"
                    value={formData.contract_type}
                    onChange={(e) => setFormData({ ...formData, contract_type: e.target.value })}
                    className="flex h-10 w-full rounded-md border border-slate-700 bg-slate-800 text-white px-3 py-2 text-sm"
                    data-testid="contract-type-select"
                  >
                    <option value="staffing">Contract Staffing</option>
                    <option value="project">Adhoc Project</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="duration" className="text-slate-200">Duration (months)</Label>
                  <Input
                    id="duration"
                    type="number"
                    value={formData.duration_months}
                    onChange={(e) => setFormData({ ...formData, duration_months: e.target.value })}
                    required
                    className="bg-slate-800 border-slate-700 text-white"
                    data-testid="contract-duration-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="start_date" className="text-slate-200">Start Date</Label>
                  <Input
                    id="start_date"
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    required
                    className="bg-slate-800 border-slate-700 text-white"
                    data-testid="contract-start-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="end_date" className="text-slate-200">End Date</Label>
                  <Input
                    id="end_date"
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    required
                    className="bg-slate-800 border-slate-700 text-white"
                    data-testid="contract-end-date-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="contract_value" className="text-slate-200">Contract Value ($)</Label>
                  <Input
                    id="contract_value"
                    type="number"
                    value={formData.contract_value}
                    onChange={(e) => setFormData({ ...formData, contract_value: e.target.value })}
                    placeholder="120000"
                    required
                    className="bg-slate-800 border-slate-700 text-white"
                    data-testid="contract-value-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="staff_cost" className="text-slate-200">Staff Cost ($)</Label>
                  <Input
                    id="staff_cost"
                    type="number"
                    value={formData.staff_cost}
                    onChange={(e) => setFormData({ ...formData, staff_cost: e.target.value })}
                    placeholder="80000"
                    required
                    className="bg-slate-800 border-slate-700 text-white"
                    data-testid="contract-staff-cost-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="staff_count" className="text-slate-200">Staff Count</Label>
                  <Input
                    id="staff_count"
                    type="number"
                    value={formData.staff_count}
                    onChange={(e) => setFormData({ ...formData, staff_count: e.target.value })}
                    required
                    className="bg-slate-800 border-slate-700 text-white"
                    data-testid="contract-staff-count-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="billing_frequency" className="text-slate-200">Billing Frequency</Label>
                <select
                  id="billing_frequency"
                  value={formData.billing_frequency}
                  onChange={(e) => setFormData({ ...formData, billing_frequency: e.target.value })}
                  className="flex h-10 w-full rounded-md border border-slate-700 bg-slate-800 text-white px-3 py-2 text-sm"
                  data-testid="contract-billing-select"
                >
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="annually">Annually</option>
                  <option value="milestone">Milestone-based</option>
                </select>
              </div>

              <Button type="submit" className="w-full" data-testid="submit-contract-button">
                Create Contract
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters & View Toggle */}
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
          <Input 
            placeholder="Search contracts..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
          />
        </div>
        <select
          value={filterUser}
          onChange={(e) => setFilterUser(e.target.value)}
          className="h-10 rounded-md border border-slate-700 bg-slate-800 text-white px-3 text-sm"
        >
          <option value="">All Sales Reps</option>
          {users.map(u => (
            <option key={u.id} value={u.id}>{u.name}</option>
          ))}
        </select>
        <div className="flex items-center gap-2">
          <span className="text-slate-400 text-sm">From:</span>
          <Input 
            type="date" 
            value={filterDateFrom} 
            onChange={(e) => setFilterDateFrom(e.target.value)}
            className="w-40 h-10 bg-slate-800 border-slate-700 text-white"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-slate-400 text-sm">To:</span>
          <Input 
            type="date" 
            value={filterDateTo} 
            onChange={(e) => setFilterDateTo(e.target.value)}
            className="w-40 h-10 bg-slate-800 border-slate-700 text-white"
          />
        </div>
        {(filterUser || filterDateFrom || filterDateTo) && (
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => {
              setFilterUser('');
              setFilterDateFrom('');
              setFilterDateTo('');
            }}
            className="text-slate-400 hover:text-white"
          >
            Clear Filters
          </Button>
        )}
        <div className="flex bg-slate-800 rounded-lg p-1">
          <Button size="sm" variant={viewMode === 'grid' ? 'default' : 'ghost'} onClick={() => setViewMode('grid')}>
            <LayoutGrid size={18} />
          </Button>
          <Button size="sm" variant={viewMode === 'list' ? 'default' : 'ghost'} onClick={() => setViewMode('list')}>
            <List size={18} />
          </Button>
        </div>
      </div>

      {/* List View */}
      {viewMode === 'list' && (
        <Card className="bg-slate-900 border-slate-800 rounded-xl overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-800">
              <tr>
                <th className="text-left p-4 text-sm font-medium text-slate-300">Client</th>
                <th className="text-left p-4 text-sm font-medium text-slate-300">Type</th>
                <th className="text-left p-4 text-sm font-medium text-slate-300">Value</th>
                <th className="text-left p-4 text-sm font-medium text-slate-300">Margin</th>
                <th className="text-left p-4 text-sm font-medium text-slate-300">Duration</th>
                <th className="text-left p-4 text-sm font-medium text-slate-300">Sales Rep</th>
                <th className="text-left p-4 text-sm font-medium text-slate-300">Status</th>
              </tr>
            </thead>
            <tbody>
              {getFilteredContracts().map((contract) => {
                const marginPercent = contract.margin_percent || 0;
                const daysRemaining = differenceInDays(new Date(contract.end_date), new Date());
                return (
                  <tr key={contract.id} className="border-t border-slate-800 hover:bg-slate-800/50">
                    <td className="p-4">
                      <div className="text-white font-medium">{contract.contact_company}</div>
                      <div className="text-slate-400 text-sm">{contract.contact_name}</div>
                    </td>
                    <td className="p-4">
                      <span className="text-xs px-2 py-1 rounded bg-primary/20 text-primary">
                        {contract.contract_type === 'staffing' ? 'Staffing' : 'Project'}
                      </span>
                    </td>
                    <td className="p-4 text-white font-medium">${contract.contract_value?.toLocaleString()}</td>
                    <td className="p-4">
                      <span className={getMarginColor(marginPercent)}>{marginPercent.toFixed(1)}%</span>
                    </td>
                    <td className="p-4 text-slate-300">{contract.duration_months} months</td>
                    <td className="p-4 text-slate-400">{getUserName(contract.assigned_to || contract.created_by)}</td>
                    <td className="p-4">
                      <span className={`text-xs px-2 py-1 rounded ${getExpiryColor(daysRemaining)}`}>
                        {daysRemaining > 0 ? `${daysRemaining}d left` : 'Expired'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {getFilteredContracts().length === 0 && (
            <div className="text-center py-12 text-slate-500">No contracts found</div>
          )}
        </Card>
      )}

      {/* Grid View */}
      {viewMode === 'grid' && (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="contracts-grid">
        {getFilteredContracts().map((contract) => {
          const margin = contract.margin || 0;
          const marginPercent = contract.margin_percent || 0;
          
          return (
            <Card
              key={contract.id}
              className="bg-slate-900 border border-slate-800 rounded-xl shadow-sm p-6"
              data-testid={`contract-card-${contract.id}`}
            >
              <div className="space-y-4">
                {/* Header */}
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-white">{contract.contact_company}</h3>
                    <p className="text-sm text-slate-400">{contract.contact_name}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-xs px-2 py-1 rounded bg-primary/20 text-primary font-medium">
                        {contract.contract_type === 'staffing' ? 'Contract Staffing' : 'Adhoc Project'}
                      </span>
                      <span className="text-xs px-2 py-1 rounded bg-slate-800 text-slate-300">
                        {contract.duration_months} months
                      </span>
                    </div>
                  </div>
                  <div className="bg-primary/20 p-3 rounded-lg">
                    <FileText size={24} className="text-primary" />
                  </div>
                </div>

                {/* Financial Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-slate-800 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <DollarSign size={16} className="text-green-400" />
                      <p className="text-xs text-slate-400">Contract Value</p>
                    </div>
                    <p className="text-xl font-bold text-white">${contract.contract_value.toLocaleString()}</p>
                  </div>
                  <div className="bg-slate-800 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <TrendingUp size={16} className={getMarginColor(marginPercent)} />
                      <p className="text-xs text-slate-400">Margin</p>
                    </div>
                    <p className={`text-xl font-bold ${getMarginColor(marginPercent)}`}>
                      {marginPercent.toFixed(1)}%
                    </p>
                    <p className="text-xs text-slate-500 mt-1">${margin.toLocaleString()}</p>
                  </div>
                </div>

                {/* Timeline */}
                <div className="bg-slate-800 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Clock size={16} className="text-slate-400" />
                      <span className="text-sm text-slate-400">Contract Period</span>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded font-medium ${getExpiryColor(contract.days_remaining)}`}>
                      {contract.days_remaining} days left
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs text-slate-500">
                    <span>{format(new Date(contract.start_date), 'MMM dd, yyyy')}</span>
                    <span>→</span>
                    <span>{format(new Date(contract.end_date), 'MMM dd, yyyy')}</span>
                  </div>
                </div>

                {/* Warning for expiring contracts */}
                {contract.expiring_soon && (
                  <div className="flex items-start gap-2 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                    <AlertTriangle size={16} className="text-amber-400 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-amber-400">Renewal Required Soon</p>
                      <p className="text-xs text-slate-400 mt-1">
                        This contract expires in {contract.days_remaining} days. Consider starting renewal discussions.
                      </p>
                    </div>
                  </div>
                )}

                {/* Additional Info */}
                <div className="flex items-center justify-between pt-3 border-t border-slate-800">
                  <div className="text-xs text-slate-500">
                    {contract.staff_count} staff • {contract.billing_frequency} billing
                  </div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
      )}

      {getFilteredContracts().length === 0 && contracts.length > 0 && (
        <Card className="bg-slate-900 border border-slate-800 rounded-xl shadow-sm p-12 text-center">
          <FileText size={48} className="mx-auto text-slate-700 mb-4" />
          <p className="text-slate-400">No contracts match your filters.</p>
        </Card>
      )}

      {contracts.length === 0 && (
        <Card className="bg-slate-900 border border-slate-800 rounded-xl shadow-sm p-12 text-center" data-testid="no-contracts">
          <FileText size={48} className="mx-auto text-slate-700 mb-4" />
          <p className="text-slate-400">No active contracts. Create your first contract to get started.</p>
        </Card>
      )}
    </div>
  );
}
